import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import {
  COUNT_CARDS_BY_CATEGORY_MAIN_PAGE,
  SortDirection,
  SortEntity,
} from 'config';
import {
  DiscountType,
  IRecentSearch,
  ISearch,
  AlertDictionaryDiscountBookmark,
  ISimilarDiscountRequest,
  IGetDiscountByCategoryParams,
  ShortDiscountContent,
  IGetLatestDiscountsForMainPage,
  IGetDiscountsForMainPage,
  IDiscountsContent,
  PatchDiscountStatusBody,
} from 'interfaces';
import { AxiosError } from 'axios';
import {
  showErrorAlert,
  showSimpleSuccessAlert,
  showSuccessAlert,
} from 'redux/utils';
import { t } from 'i18next';
import {
  getDiscountsByCategory,
  getDiscountById,
  getDiscountInfo,
  getDiscountsForMainPage,
  getLatestDiscountsForMainPage,
  fetchRecentSearchRequest,
  getSearchDiscountsResults,
  deleteDiscountById,
  fetchSimilarDiscounts,
  deleteAllRecentSearchDiscounts,
  deleteRecentSearchDiscountByID,
  archiveDiscountById,
  patchDiscountStatus,
} from 'services/discounts';
import {
  DiscountTableFilters,
  DiscountTableSort,
} from 'view/pages/Statistics/components/DiscountStatistics/DiscountStatistics.model';
import { AppThunkAction } from '../../store';
import { IDiscountState } from './discountsTypes';

const initialState: IDiscountState = {
  discounts: undefined,
  discountsIsLoading: false,
  latestDiscountsForMainPage: undefined,
  searchResults: undefined,
  recentSearch: undefined,
  discountsByCategory: undefined,
  discountsForMainPage: undefined,
  discountById: undefined,
  currentSortField: SortEntity.dateBegin,
  currentSortDirection: SortDirection.DESC,
  similarDiscounts: undefined,
  discountInfo: undefined,
  discountStatisticsState: {},
};

export const discountsReducer = createSlice({
  name: 'discounts',
  initialState,
  reducers: {
    fetchDiscounts: (state, action) => {
      state.discounts = action.payload;
    },
    setDiscountsForMainPage: (state, action) => {
      state.discountsForMainPage = action.payload;
    },
    setLatestDiscountsForMainPage: (
      state,
      action: PayloadAction<ShortDiscountContent[]>,
    ) => {
      state.latestDiscountsForMainPage = action.payload;
    },
    resetDiscounts: (state) => {
      state.discounts = undefined;
    },
    startFetchDiscounts: (state) => {
      state.discountsIsLoading = true;
    },
    endFetchDiscounts: (state) => {
      state.discountsIsLoading = false;
    },
    setSearchResults: (state, action) => {
      state.searchResults = action.payload;
    },
    resetSearchResults: (state) => {
      state.searchResults = undefined;
    },
    setSimilarDiscounts: (state, action) => {
      state.similarDiscounts = action.payload;
    },
    discountChangeSortField: (state, action) => {
      state.currentSortField = action.payload;
    },
    discountChangeSortDirection: (state, action) => {
      state.currentSortDirection = action.payload;
    },
    fetchDiscountsByCategory: (state, action) => {
      state.discountsByCategory = action.payload;
    },
    fetchDiscountById: (state, action) => {
      state.discountById = action.payload;
    },
    fetchDiscountInfo: (state, action) => {
      state.discountInfo = action.payload;
    },
    recentSearch: (state, action) => {
      state.recentSearch = action.payload;
    },
    resetDiscountById: (state) => {
      state.discountById = undefined;
    },
    setDiscountStatisticsSort: (
      state,
      action: PayloadAction<DiscountTableSort | undefined>,
    ) => {
      state.discountStatisticsState = {
        ...state.discountStatisticsState,
        sort: action.payload,
      };
    },
    setDiscountStatisticsFilters: (
      state,
      action: PayloadAction<DiscountTableFilters | undefined>,
    ) => {
      state.discountStatisticsState = {
        ...state.discountStatisticsState,
        filters: action.payload,
      };
    },
  },
});

export const {
  fetchDiscounts,
  setDiscountsForMainPage,
  setLatestDiscountsForMainPage,
  resetDiscounts,
  startFetchDiscounts,
  endFetchDiscounts,
  setSearchResults,
  resetSearchResults,
  setSimilarDiscounts,
  discountChangeSortField,
  discountChangeSortDirection,
  fetchDiscountsByCategory,
  fetchDiscountById,
  fetchDiscountInfo,
  recentSearch,
  resetDiscountById,
  setDiscountStatisticsSort,
  setDiscountStatisticsFilters,
} = discountsReducer.actions;

export default discountsReducer.reducer;

export const searchDiscount =
  (searchValue: string, locationId: number): AppThunkAction =>
  async (dispatch) => {
    dispatch(resetSearchResults());

    dispatch(startFetchDiscounts());

    try {
      const results = <ISearch>(
        await getSearchDiscountsResults(searchValue, locationId)
      );

      dispatch(setSearchResults(results));

      dispatch(endFetchDiscounts());
    } catch (e) {
      const err = e as AxiosError;

      if (err.response?.status === 404) {
        dispatch(setSearchResults({ totalElements: 0, discounts: [] }));
      } else {
        dispatch(resetSearchResults());
      }
    }
  };

export const searchSimilarDiscount =
  (params: ISimilarDiscountRequest): AppThunkAction =>
  async (dispatch) => {
    const { discounts } = await fetchSimilarDiscounts(params);

    discounts && dispatch(setSimilarDiscounts(discounts));
  };

export const getDiscountByCategory =
  (params: IGetDiscountByCategoryParams): AppThunkAction =>
  async (dispatch) => {
    const discounts = await getDiscountsByCategory(params);
    dispatch(fetchDiscountsByCategory(discounts));
  };

export const getChosenDiscount =
  (id: string, userId: number, onFailAction: () => void): AppThunkAction =>
  async (dispatch) => {
    try {
      const discount = await getDiscountById(Number(id), userId);

      dispatch(fetchDiscountById(discount));
    } catch (e) {
      onFailAction();
    }
  };

export const getDiscountInfoForModal =
  (discountId: number): AppThunkAction =>
  async (dispatch) => {
    try {
      const discountInfo = await getDiscountInfo(discountId);

      dispatch(fetchDiscountInfo(discountInfo));
    } catch (e) {
      throw new Error('Error');
    }
  };

export const getDiscountsForMainPageThunk =
  (params: IGetDiscountsForMainPage): AppThunkAction =>
  async (dispatch) => {
    dispatch(startFetchDiscounts());

    const discounts = await getDiscountsForMainPage(params);

    dispatch(setDiscountsForMainPage(discounts));

    dispatch(endFetchDiscounts());
  };

export const getLatestDiscountsForMainPageThunk =
  (params: IGetLatestDiscountsForMainPage): AppThunkAction =>
  async (dispatch) => {
    const { content: discounts } = await getLatestDiscountsForMainPage(params);

    dispatch(
      setLatestDiscountsForMainPage(
        discounts.map((v) => ({
          ...v,
          image: v.imageDiscount,
          companyName: v.company.title,
        })),
      ),
    );
  };

export const fetchRecentSearch = (): AppThunkAction => async (dispatch) => {
  dispatch(startFetchDiscounts());

  const recentSearchItems =
    (await fetchRecentSearchRequest()) as IRecentSearch[];

  if (recentSearchItems.length > 0) {
    dispatch(recentSearch(recentSearchItems));
  } else {
    dispatch(recentSearch(undefined));
  }
  dispatch(endFetchDiscounts());
};

export const checkDiscountsForMainPageAndExecute =
  (
    cityId: number,
    userId: number,
    onSuccessAction: () => void,
    onFailAction?: () => void,
  ): AppThunkAction =>
  async (dispatch) => {
    dispatch(startFetchDiscounts());

    try {
      const discounts = await getDiscountsForMainPage({
        cityId,
        userId,
        discountsPerCategory: COUNT_CARDS_BY_CATEGORY_MAIN_PAGE,
      });

      if (discounts.length) {
        dispatch(setDiscountsForMainPage(discounts));

        onSuccessAction();
      } else {
        throw new Error();
      }
    } catch (e) {
      onFailAction && onFailAction();
    } finally {
      dispatch(endFetchDiscounts());
    }
  };

export const deleteDiscount =
  (
    discountId: string,
    discountType: DiscountType,
    discountName: string,
    onSuccess: () => void,
  ): AppThunkAction =>
  async (dispatch) => {
    try {
      await deleteDiscountById(discountId);

      onSuccess();

      showSuccessAlert({
        dispatch,
        discountType,
        discountTitle: discountName,
        operation: AlertDictionaryDiscountBookmark.delete,
      });
    } catch (e) {
      showErrorAlert(
        dispatch,
        e,
        t(['alert', discountType, 'delete', 'error', 'title'].join('.')),
      );
    }
  };

export const archiveDiscount =
  (
    discountId: string,
    discountType: DiscountType,
    discountName: string,
    onSuccess: () => void,
  ): AppThunkAction =>
  async (dispatch) => {
    try {
      await archiveDiscountById(discountId);

      onSuccess();

      showSuccessAlert({
        dispatch,
        discountType,
        discountTitle: discountName,
        operation: AlertDictionaryDiscountBookmark.archive,
      });
    } catch (e) {
      showErrorAlert(
        dispatch,
        e,
        t(['alert', discountType, 'archive', 'error', 'title'].join('.')),
      );
    }
  };

export const updateDiscountStatus =
  (
    discountId: string,
    payload: PatchDiscountStatusBody,
    onSuccess?: (discount: IDiscountsContent) => void,
    successMessage?: string,
  ): AppThunkAction =>
  async (dispatch, getState) => {
    try {
      const updated = await patchDiscountStatus(discountId, payload);
      const current = getState().discounts?.discountById;

      if (current && String(current.id) === String(discountId)) {
        dispatch(fetchDiscountById({ ...current, ...updated }));
      }

      if (successMessage) {
        showSimpleSuccessAlert(
          dispatch,
          t('archives.statusUpdateSuccessTitle'),
          successMessage,
        );
      }

      onSuccess?.(updated);
    } catch (e) {
      showErrorAlert(dispatch, e, t('archives.statusUpdateError'));
    }
  };

export const deleteAllRecentSearch = (): AppThunkAction => async (dispatch) => {
  try {
    await deleteAllRecentSearchDiscounts();
    dispatch(recentSearch(undefined));
  } catch (e) {
    throw new Error('Error');
  }
};

export const deleteRecentSearchByID =
  (id: string): AppThunkAction =>
  async (dispatch) => {
    try {
      await deleteRecentSearchDiscountByID(id);
      dispatch(fetchRecentSearch());
    } catch (e) {
      throw new Error('Error');
    }
  };
